import React, { useEffect, useState } from 'react';
import { MaintenanceLogItem } from '../../services/api';
import toast from 'react-hot-toast';

interface MaintenanceDetailsModalProps {
  requisition: MaintenanceLogItem | null;
  isOpen: boolean;
  onClose: () => void;
}

interface EquipmentItem {
  asset: string;
  equipment: {
    serial_number: string;
    category: string;
    subcategory: string;
  };
  descriptions: string[];
  completed: boolean;
}

interface AssetPair {
  asset: string;
  descriptions: string[];
  completed: boolean;
}

const MaintenanceDetailsModal: React.FC<MaintenanceDetailsModalProps> = ({
  requisition,
  isOpen,
  onClose,
}) => {
  const [equipmentItems, setEquipmentItems] = useState<EquipmentItem[]>([]);
  const [assetPairs, setAssetPairs] = useState<AssetPair[]>([]);

  useEffect(() => {
    if (requisition) {
      // Use equipmentItems from API (already processed by backend)
      const processedEquipment: EquipmentItem[] = [];
      if (requisition.equipmentItems && requisition.equipmentItems.length > 0) {
        requisition.equipmentItems.forEach((item) => {
          // Find the corresponding equipmentRequest to get completion status
          const equipmentRequest = requisition.equipmentRequests?.find((req) =>
            req.equipment?.description?.includes(item.asset.split(' (S/N:')[0])
          );
          
          processedEquipment.push({
            asset: item.asset,
            equipment: item.equipment,
            descriptions: item.descriptions || [],
            completed: equipmentRequest?.completed || false,
          });
        });
      }
      setEquipmentItems(processedEquipment);

      // Use assetDescriptionPairs from API (already processed by backend)
      const processedAssets: AssetPair[] = [];
      if (requisition.assetDescriptionPairs && requisition.assetDescriptionPairs.length > 0) {
        requisition.assetDescriptionPairs.forEach((pair) => {
          // Find the corresponding asset to get completion status
          const asset = requisition.assets?.find((a) => a.asset === pair.asset);
          
          processedAssets.push({
            asset: pair.asset,
            descriptions: pair.descriptions || [],
            completed: asset?.completed || false,
          });
        });
      }
      setAssetPairs(processedAssets);
    }
  }, [requisition]);

  if (!isOpen || !requisition) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            className="relative bg-white dark:bg-midnight-800 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden border border-slate-200 dark:border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-brand-500 to-brand-600 dark:from-brand-600 dark:to-brand-700 px-6 py-4 border-b border-brand-600 dark:border-brand-700 z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  ATR - {requisition.stores.map((s) => s.name).join(', ')}
                </h3>
                <button
                  onClick={onClose}
                  className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
              {/* Equipment Items Section */}
              {equipmentItems.length > 0 && (
                <div className="bg-slate-50 dark:bg-midnight-900/50 rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden">
                  <div className="bg-gradient-to-r from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-700 px-4 py-3">
                    <h5 className="font-bold text-white flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                      </svg>
                      Equipment Items
                    </h5>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 dark:divide-white/10">
                      <thead className="bg-slate-100 dark:bg-midnight-900/70">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                            Equipment
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                            Serial Number
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                            Repair Description
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-midnight-800/40 divide-y divide-slate-200 dark:divide-white/5">
                        {equipmentItems.map((item, index) => (
                          <tr key={index} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                            <td className="px-4 py-3">
                              <div>
                                <div className="font-semibold text-slate-900 dark:text-slate-100">{item.asset}</div>
                                <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                                  Category: {item.equipment.category} | Subcategory: {item.equipment.subcategory}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                              {item.equipment.serial_number}
                            </td>
                            <td className="px-4 py-3">
                              {item.descriptions.length > 0 ? (
                                <ul className="list-disc list-inside text-sm text-slate-700 dark:text-slate-300 space-y-1">
                                  {item.descriptions.map((desc, i) => (
                                    <li key={i}>{desc}</li>
                                  ))}
                                </ul>
                              ) : (
                                <em className="text-sm text-slate-500 dark:text-slate-400">No description</em>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {item.completed ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                      fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  Completed
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                      fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  Pending
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Other Assets Section */}
              {assetPairs.length > 0 && (
                <div className="bg-slate-50 dark:bg-midnight-900/50 rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 dark:from-indigo-600 dark:to-indigo-700 px-4 py-3">
                    <h5 className="font-bold text-white flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                      Other Assets
                    </h5>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 dark:divide-white/10">
                      <thead className="bg-slate-100 dark:bg-midnight-900/70">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                            Asset to be repaired
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                            Description of repair
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-midnight-800/40 divide-y divide-slate-200 dark:divide-white/5">
                        {assetPairs.map((pair, index) => (
                          <tr key={index} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                            <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{pair.asset}</td>
                            <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                              {pair.descriptions.length > 0 ? (
                                pair.descriptions.join(', ')
                              ) : (
                                <em className="text-slate-500 dark:text-slate-400">No description</em>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {pair.completed ? (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                      fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  Completed
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30">
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                      fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  Pending
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Empty State */}
              {equipmentItems.length === 0 && assetPairs.length === 0 && (
                <div className="text-center py-12">
                  <svg
                    className="mx-auto h-12 w-12 text-slate-400 dark:text-slate-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">No items found for this requisition</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-slate-50 dark:bg-midnight-900/50 px-6 py-4 border-t border-slate-200 dark:border-white/10">
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg font-medium transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MaintenanceDetailsModal;
